import React from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Target, Zap, Lightbulb, TrendingUp, Quote } from 'lucide-react';

export default function AboutUs() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">About Automation Prime</Badge>
              <h1 className="text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                We Build Businesses That Scale
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We're operators, builders, and automation obsessives on a mission to prove that 95% automated businesses can be cashflow positive in under 6 months.
              </p>
            </div>
            
            <Card className="rounded-xl mb-12 bg-gradient-to-br from-primary/5 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardContent className="pt-8 pb-8">
                <Quote className="h-10 w-10 mb-4" style={{ color: 'var(--ap-blue)' }} />
                <p className="text-xl md:text-2xl leading-relaxed mb-6">
                  Most companies scale by adding more people. We scale by building better systems. That's the difference between linear growth and exponential growth.
                </p>
                <p className="text-muted-foreground">
                  We don't just invest in companies—we transform them. Every business in our portfolio is rebuilt from the ground up with scalable automation at its core.
                </p>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="rounded-xl">
                <CardHeader>
                  <Target className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle>Our Mission</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Build and scale 50+ highly automated businesses that prove efficiency and profitability aren't mutually exclusive—they're inseparable.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <Zap className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle>95% Automation Target</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    We systematically automate operations, customer service, sales, and reporting—leaving humans to focus only on strategy, creativity, and high-value decisions.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <TrendingUp className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle>6-Month Cashflow Positive</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Our aggressive timeline forces discipline. If a business can't become cashflow positive within 6 months, the model isn't efficient enough.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <Lightbulb className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle>Scalable by Design</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Every system, process, and workflow is built to scale. Going from 10 customers to 10,000 shouldn't require 1,000x the effort—or the headcount.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <Card className="rounded-xl mb-8 bg-muted/30">
              <CardHeader>
                <CardTitle className="text-2xl">What We Believe</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div>
                  <p className="font-semibold text-foreground mb-2">Automation isn't the future—it's the present.</p>
                  <p>Companies that resist automation will be outpaced by those that embrace it. The question isn't "if" but "how fast."</p>
                </div>
                
                <div>
                  <p className="font-semibold text-foreground mb-2">People are the secret weapon.</p>
                  <p>Automation doesn't replace people—it multiplies their impact. When you remove repetitive tasks, teams can focus on innovation, strategy, and growth.</p>
                </div>
                
                <div>
                  <p className="font-semibold text-foreground mb-2">Profitability is a design choice.</p>
                  <p>We don't chase vanity metrics. Every decision is measured against one question: "Does this move us closer to cashflow positive?"</p>
                </div>
                
                <div>
                  <p className="font-semibold text-foreground mb-2">Speed matters.</p>
                  <p>The faster we can launch, test, and iterate, the faster we learn what works. Our 6-month timeline isn't arbitrary—it's intentional.</p>
                </div>
              </CardContent>
            </Card>
            
            <div className="text-center">
              <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Leadership</h3>
              <Card className="rounded-xl inline-block max-w-md">
                <CardContent className="pt-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-2xl font-bold" style={{ color: 'var(--ap-blue)' }}>
                    AD
                  </div>
                  <h4 className="text-xl font-semibold mb-1">Andrew Doucet</h4>
                  <p className="text-sm mb-3" style={{ color: 'var(--ap-blue)' }}>Founder & President</p>
                  <p className="text-sm text-muted-foreground">
                    Leading the charge to build a new generation of businesses where automation and profitability go hand in hand.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Important Disclaimer */}
            <Card className="rounded-xl mt-12 bg-muted/30 border-2 border-muted">
              <CardHeader>
                <CardTitle className="text-xl">Important Notice</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  At this time and for the next 3 years, Automation Prime will not be providing any type of staffing services within the life sciences space, nor will we be investing in any companies that provide those services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
